/**
 * Phone sign-in for the web login (/app/login). Numbers are checked against
 * existing accounts first, so no SMS is sent to a number without one (accounts
 * are created in the iPhone app). The code is sent behind an invisible
 * reCAPTCHA. Signing in updates the app through the existing Firebase auth
 * listener, like email sign-in.
 */
import axios from 'axios';
import { ConfirmationResult, RecaptchaVerifier, signInWithPhoneNumber, UserCredential } from 'firebase/auth';
import { auth } from './firebase';

/**
 * E.164 from what someone typed: a leading + is taken as given; otherwise
 * 10 digits, or 11 starting with 1, are a US/Canada number.
 */
export function toE164(input: string): string | null {
  const trimmed = input.trim();
  const digits = trimmed.replace(/\D/g, '');
  if (trimmed.startsWith('+')) return digits.length >= 8 ? `+${digits}` : null;
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  return null;
}

/**
 * A plain-words message for a Firebase phone sign-in error code. Setup
 * problems (domain not authorized, phone sign-in off, reCAPTCHA) point to
 * email sign-in, and anything unexpected shows the code, so a screenshot is
 * enough to diagnose it. Same wording as the OAuth sign-in page.
 */
export function phoneErrorMessage(code: string | undefined): string {
  const suffix = code ? ` (${code})` : '';
  switch (code) {
    case 'auth/invalid-verification-code':
      return 'That code did not work. Check it and try again.';
    case 'auth/code-expired':
      return 'That code has expired. Go back and send a new one.';
    case 'auth/invalid-phone-number':
    case 'auth/missing-phone-number':
      return 'That number does not look right. Check it and try again.';
    case 'auth/too-many-requests':
    case 'auth/quota-exceeded':
      return 'Too many attempts. Please wait a few minutes and try again.';
    case 'auth/network-request-failed':
      return 'Could not reach Stash. Check your connection and try again.';
    case 'auth/unauthorized-domain':
    case 'auth/operation-not-allowed':
    case 'auth/invalid-app-credential':
    case 'auth/missing-app-credential':
    case 'auth/captcha-check-failed':
    case 'auth/app-not-authorized':
      return `Sign-in by text is not available here right now. Please sign in with your email instead.${suffix}`;
    default:
      return `We could not sign you in by text. Please try again.${suffix}`;
  }
}

/** One phone sign-in attempt: send a code, then confirm it. */
export class PhoneSignIn {
  private verifier: RecaptchaVerifier | null = null;
  private confirmation: ConfirmationResult | null = null;

  /** Whether an account uses this number (POST /api/auth-pre-check/phone-exists). */
  async hasAccount(phoneNumber: string): Promise<boolean> {
    const response = await axios.post<{ data: boolean }>('/api/auth-pre-check/phone-exists', { phone_number: phoneNumber });
    return response.data.data === true;
  }

  /** Sends the SMS code. `containerId` is an element the invisible reCAPTCHA can use. */
  async sendCode(phoneNumber: string, containerId: string): Promise<void> {
    this.verifier = this.verifier ?? new RecaptchaVerifier(auth, containerId, { size: 'invisible' });
    try {
      this.confirmation = await signInWithPhoneNumber(auth, phoneNumber, this.verifier);
    } catch (error) {
      // A used or failed reCAPTCHA can't be reused; start the next try clean.
      this.dispose();
      throw error;
    }
  }

  async confirm(code: string): Promise<UserCredential> {
    if (!this.confirmation) throw new Error('Send a code first.');
    return this.confirmation.confirm(code);
  }

  dispose(): void {
    this.verifier?.clear();
    this.verifier = null;
  }
}
